import type * as ecModels from '../../../models'

import type { Config } from './config'
import type * as models from './models'

/**
 * {@link https://ngrx.io/docs State} of this plugin
 */
export interface State<
  TTypeParams extends
    ecModels.TypeParams.Constraint = ecModels.TypeParams.Constraint,
> {
  /**
   * Cached entities
   *
   * @see {@link models.Lookup}
   */
  entities: models.Lookup<TTypeParams>
  /**
   * Used to determine which entites to purge when max number of entities are to
   * be exceeded
   */
  lastWriteTimes: Record<ecModels.EntityId, number>
  /**
   * Number of cached entities
   */
  cachedCount: number
}
export namespace State {
  export const INITIAL: State<any> = {
    cachedCount: 0,
    entities: {},
    lastWriteTimes: {},
  }

  /**
   * Puts one or more entities into the cache {@link State}
   */
  export function put<TTypeParams extends ecModels.TypeParams>(
    state: State<TTypeParams>,
    { maxEntities }: Config,
    ...insertions: readonly {
      readonly id: ecModels.EntityId
      readonly entity: TTypeParams['entity']
    }[]
  ): State<TTypeParams> {
    if (!insertions.length) {
      // Nothing to do
      return state
    }
    const updates = insertions
      .filter(({ id }) => !!state.entities[id])
      // NOTE: slice should never be necessary, but in case somehow there are more
      // entities in the cache than are allowed, this prevents resulting bugs
      .slice(0, maxEntities)
    insertions = [
      // We always insert updates first, since we know those entities have
      // already been desired elsewhere. E.g., this prevents looking at a
      // paginated list of entities in a table, downloading all entities, and
      // all of sudden the list you were looking at is gone
      ...updates,
      // Ensures that we don't try to insert more in the cache than is allowed
      ...insertions.slice(0, maxEntities - updates.length),
    ]
    const projectedNumberOfEntities =
      state.cachedCount + insertions.length - updates.length
    state =
      projectedNumberOfEntities > maxEntities
        ? // If we have to purge some, we do that here (also creates a shallow copy
          // for us)
          State.removeOldest(state, projectedNumberOfEntities - maxEntities, {
            ignoring: updates.map(update => update.id),
          })
        : // If we don't have to purge any, we still need to create a shallow copy
          // that we can modify
          {
            ...state,
            entities: { ...state.entities },
            lastWriteTimes: { ...state.lastWriteTimes },
          }
    const now = Date.now()
    for (const { entity, id } of insertions) {
      if (!state.entities[id]) {
        // If this is a new entity, we increment the count of cached
        // entities
        state.cachedCount++
      }
      // Then we put the entity in the lookup
      state.entities[id] = entity
      state.lastWriteTimes[id] = now
    }
    return state
  }

  /**
   * Removes one or more entities from the cache {@link State}
   */
  export function remove<TTypeParams extends ecModels.TypeParams>(
    state: State<TTypeParams>,
    ...ids: readonly ecModels.EntityId[]
  ): State<TTypeParams> {
    if (!ids.length) {
      // Nothing to do
      return state
    }
    // First need to create a copy that we can update
    state = {
      ...state,
      entities: { ...state.entities },
      lastWriteTimes: { ...state.lastWriteTimes },
    }
    for (const id of ids) {
      if (state.entities[id]) {
        // If the entity exists, we decrement the count of cached entities
        state.cachedCount--
        // And remove the id/entity from the lookup
        delete state.entities[id]
        delete state.lastWriteTimes[id]
      }
    }
    return state
  }

  /**
   * Removes the oldest given number of entities from the cache {@link State}
   */
  export function removeOldest<TTypeParams extends ecModels.TypeParams>(
    state: State<TTypeParams>,
    removeCount: number,
    {
      ignoring = [],
    }: {
      /**
       * List of {@link ecModels.EntityId} that should not be removed
       */
      readonly ignoring?: readonly ecModels.EntityId[]
    } = {},
  ): State<TTypeParams> {
    if (removeCount <= 0) {
      // Nothing to remove
      return state
    }
    const ignoringSet = new Set(ignoring)
    /**
     * Sorted from oldest to least old
     */
    const oldestIds: ecModels.EntityId[] = []
    for (const id in state.lastWriteTimes) {
      if (ignoringSet.has(id)) {
        // Skips ignored IDs
        continue
      }
      let leastOldId = id
      let i: number
      // First, we have to figure out where this id would go in the list (based
      // on oldness)
      for (
        i = oldestIds.length - 1;
        i >= 0 && state.lastWriteTimes[id] < state.lastWriteTimes[oldestIds[i]];
        i--
      ) {}
      if (i < oldestIds.length - 1) {
        // If the id is old enough to belong somewhere in the list, we need to
        // insert it in the right position. i is currently the first position
        // that was older than this id, so we first need to increment it one
        // position
        for (i++; i < oldestIds.length; i++) {
          // Then, we sequentially swap each item into the next position in the list
          const temp = leastOldId
          leastOldId = oldestIds[i]
          oldestIds[i] = temp
        }
      }
      if (oldestIds.length < removeCount) {
        // Finally, if there is still space in the list, we can add the least
        // old id to the end of the list
        oldestIds.push(leastOldId)
      }
    }
    return State.remove(state, ...oldestIds)
  }
}
